import { useLocation } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import 'animate.css'
import { DateTime } from 'luxon'
import { OverviewContextProvider } from '../../context/OverviewContextProvider'
import { useDeferredInitialLoad, useEventBanners, usePageFocus } from '../../hooks'
import { useIsFeatureEnabled } from '../../services/api/feature-flags.hooks'
import { useGetFolders } from '../../services/api/folders.hooks'
import { useGetOverviewViews } from '../../services/api/overview.hooks'
import { useFetchExternalTasks } from '../../services/api/tasks.hooks'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { CalendarContextProvider } from '../calendar/CalendarContext'
import { CalendarPlanProvider } from '../calendar/CalendarPlanContext'
import GoalCreationFlows from '../goals/creation/GoalCreationFlows'
import { GoalCreationProvider } from '../goals/creation/shared/GoalCreationContext'
import DragLayer from '../molecules/DragLayer'
import DefaultTemplate from '../templates/DefaultTemplate'
import DailyOverviewView from '../views/DailyOverviewView'
import GoalsView from '../views/GoalsView'
import NoteListView from '../views/NoteListView'
import RecurringTasksView from '../views/RecurringTasksView'
import RoutinesView from '../views/RoutinesView'
import TaskSection from '../views/TaskSectionView'

const MainScreen = () => {
    const location = useLocation()
    const shouldLoadSecondaryQueries = useDeferredInitialLoad()
    useGetUserInfo()
    useGetTasksV4()
    useGetFolders()
    useGetOverviewViews()
    useFetchExternalTasks(shouldLoadSecondaryQueries)
    useEventBanners(DateTime.now(), shouldLoadSecondaryQueries)
    usePageFocus(true)
    const areGoalsEnabled = useIsFeatureEnabled('goals')

    const currentPage = () => {
        switch (location.pathname.split('/')[1]) {
            case 'overview':
                return (
                    <OverviewContextProvider>
                        <DailyOverviewView />
                    </OverviewContextProvider>
                )
            case 'goals':
                return <GoalsView />
            case 'recurring-tasks':
                return <RecurringTasksView />
            case 'routines':
                return <RoutinesView />
            case 'notes':
                return <NoteListView />
            case 'tasks':
                return <TaskSection />
            default:
                return (
                    <OverviewContextProvider>
                        <DailyOverviewView />
                    </OverviewContextProvider>
                )
        }
    }

    return (
        <CalendarContextProvider>
            <CalendarPlanProvider>
                <GoalCreationProvider>
                    <DefaultTemplate>{currentPage()}</DefaultTemplate>
                    {/* One host for goal creation, so any page under the provider can open the flow.
                        It renders nothing until openFlow(); the lab switcher inside is self-gating. */}
                    {areGoalsEnabled && <GoalCreationFlows />}
                </GoalCreationProvider>
                <DragLayer />
            </CalendarPlanProvider>
        </CalendarContextProvider>
    )
}

export default MainScreen
